// CR: Canonical helper for extracting validated deal from validated_payload_json
// The payload can be stored in two formats:
// 1. Wrapped: { extracted_deal: {...deal...}, validation_meta: {...} }
// 2. Direct: {...deal...} (legacy format)
// This helper ensures we always get the actual deal object.
//
// ============================================================================
// CANONICAL SQL UNWRAP SNIPPET
// ============================================================================
// When querying cr075_staging_deal_items directly in SQL, use this pattern to
// correctly unwrap validated_payload_json (mirrors getValidatedDeal() logic):
//
//   -- Canonical "effective deal" for validated payloads
//   coalesce(
//     validated_payload_json->'extracted_deal',
//     validated_payload_json
//   ) as validated_deal_effective
//
// Then access fields from validated_deal_effective:
//
//   validated_deal_effective->>'headline_mechanic' as headline,
//   validated_deal_effective->>'deal_type' as deal_type,
//   validated_deal_effective->'variants' as variants,
//   validated_deal_effective->'dates' as dates,
//   validated_deal_effective->'value' as value_obj
//
// To compute variant count:
//
//   jsonb_array_length(
//     coalesce(
//       coalesce(validated_payload_json->'extracted_deal', validated_payload_json)->'variants',
//       '[]'::jsonb
//     )
//   ) as variants_len
//
// Full example query:
//
//   with staged as (
//     select
//       id,
//       merchant_key,
//       extraction_method,
//       coalesce(validated_payload_json->'extracted_deal', validated_payload_json) as vdeal
//     from cr075_staging_deal_items
//     where batch_run_id = 'YOUR_BATCH_RUN_ID'
//   )
//   select
//     id,
//     merchant_key,
//     vdeal->>'deal_type' as deal_type,
//     vdeal->>'headline_mechanic' as headline,
//     jsonb_array_length(coalesce(vdeal->'variants', '[]'::jsonb)) as variants_len,
//     vdeal->'value' as value_obj,
//     vdeal->'dates' as dates_obj,
//     vdeal->'diagnostics' as diagnostics
//   from staged
//   where extraction_method = 'AI_DEEP_SCAN';
//
// ============================================================================

/**
 * Extracted deal interface matching the full AI extraction output
 * CR228: Extended with points-back offer fields
 */
export interface ExtractedDeal {
  // Core identity
  deal_type?: string;
  candidate_id?: string;
  candidate_signature?: string;
  
  // Purchase info
  purchase_brand?: string;
  purchase_gift_card_brand?: string; // Legacy alias
  purchase_amount?: number;
  qualifying_spend_cad?: number; // Legacy alias
  merchant?: string;
  
  // Value / reward info
  reward_type?: string;
  reward_amount?: number;
  reward_merchant?: string;
  bonus_value_cad?: number; // Legacy alias
  percent_off?: number;
  discount_percent?: number; // Legacy alias
  points_amount?: number;
  points_program?: string;
  points_program_id?: string; // Legacy alias
  
  // CR228: Points-back offer fields
  spend_amount?: number;
  spend_currency?: string;
  points_cash_value_amount?: number | null;
  effective_rebate_percent?: number | null;
  offer_frequency?: "per_spend_threshold" | "one_time" | null;
  eligibility_flags?: string[];
  eligible_brands?: string[];
  
  // Dates
  dates?: {
    valid_from?: string | null;
    valid_until?: string | null;
    raw_text?: string | null;
  };
  valid_from?: string; // Legacy flat format
  valid_until?: string; // Legacy flat format
  
  // Value object (normalized format)
  value?: {
    bonus_value_cad?: number | null;
    qualifying_spend_cad?: number | null;
    percent_off?: number | null;
    points_amount?: number | null;
    reward_text?: string | null;
    // CR228: Points-back in value object
    points_cash_value_amount?: number | null;
    effective_rebate_percent?: number | null;
  };
  
  // Fine print / raw text
  fine_print_raw?: string | null;
  fine_print_clean_bullets?: string[];
  raw_text_excerpt?: string | null;
  raw_offer_text?: string; // Legacy
  headline_mechanic?: string;
  notes?: string;
  
  // URLs
  extracted_urls?: string[];
  claim_url?: string | null;
  
  // Variants (multi-brand explosion)
  variants?: Array<{
    variant_type?: string;
    purchase_brand?: string;
    brand_source?: string;
    brand_evidence?: string[];
  }>;
  is_multi_brand_variant?: boolean;
  variant_notes?: string;
  
  // Brands object (normalized)
  brands?: {
    purchase_brand?: string | null;
    bonus_brand?: string | null;
  };
  
  // Display helpers
  confidence?: number;
  
  // Diagnostics
  diagnostics?: {
    ai_call_id?: string;
    date_alignment?: unknown;
    ai_input?: unknown;
    validation_flags?: unknown;
    used_image?: boolean;
    used_ocr?: boolean;
    has_variants?: boolean;
    variant_count?: number;
    // CR228: Points parsing diagnostics
    points_parse_result?: unknown;
  };
}

/**
 * Validation meta interface
 */
export interface ValidationMeta {
  is_publishable?: boolean;
  missing_fields?: string[];
  warnings?: string[];
  flags?: Record<string, unknown>;
}

/**
 * Validated payload wrapper structure
 */
export interface ValidatedPayloadWrapper {
  extracted_deal?: ExtractedDeal;
  extracted_deal_summary?: Record<string, unknown>;
  validation_meta?: ValidationMeta;
  extraction_metrics?: Record<string, unknown>;
  // Legacy fields that may exist at top level
  deal_type?: string;
  headline_mechanic?: string;
  [key: string]: unknown;
}

/**
 * Canonical helper to unwrap the validated deal object from validated_payload_json.
 * 
 * Rules:
 * - If validated_payload_json.extracted_deal exists, treat that as the deal object.
 * - Else treat validated_payload_json itself as the deal object (backwards compatible).
 * 
 * @param payload The validated_payload_json value from cr075_staging_deal_items
 * @returns The actual deal object with all extracted fields
 */
export function getValidatedDeal(payload: unknown): ExtractedDeal {
  if (!payload || typeof payload !== 'object') {
    return {};
  }
  
  const wrapper = payload as ValidatedPayloadWrapper;
  
  // If extracted_deal exists, return it as the canonical deal
  if (wrapper.extracted_deal && typeof wrapper.extracted_deal === 'object') {
    return wrapper.extracted_deal;
  }
  
  // Fallback: treat the entire payload as the deal object (legacy format)
  return payload as ExtractedDeal;
}

/**
 * Get validation metadata from the payload
 */
export function getValidationMeta(payload: unknown): ValidationMeta {
  if (!payload || typeof payload !== 'object') {
    return {};
  }
  
  const wrapper = payload as ValidatedPayloadWrapper;
  return wrapper.validation_meta || {};
}

/**
 * Check if a payload is in the wrapped format (has extracted_deal)
 */
export function isWrappedPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  return 'extracted_deal' in (payload as Record<string, unknown>);
}

/**
 * Get the deal type with proper unwrapping
 */
export function getDealType(payload: unknown): string | undefined {
  const deal = getValidatedDeal(payload);
  return deal.deal_type;
}

/**
 * Get the headline/offer text with proper unwrapping
 */
export function getHeadlineMechanic(payload: unknown): string | undefined {
  const deal = getValidatedDeal(payload);
  return deal.headline_mechanic || deal.raw_offer_text;
}

/**
 * Get the purchase brand with proper unwrapping (handles multiple field names)
 */
export function getPurchaseBrand(payload: unknown): string | undefined {
  const deal = getValidatedDeal(payload);
  return deal.purchase_brand || 
         deal.purchase_gift_card_brand || 
         deal.brands?.purchase_brand || 
         undefined;
}

/**
 * Get variants array with proper unwrapping
 */
export function getVariants(payload: unknown): ExtractedDeal['variants'] {
  const deal = getValidatedDeal(payload);
  return deal.variants || [];
}

/**
 * Get dates object with proper unwrapping
 */
export function getDates(payload: unknown): { valid_from?: string | null; valid_until?: string | null; raw_text?: string | null } {
  const deal = getValidatedDeal(payload);
  
  // Prefer nested dates object
  if (deal.dates) {
    return {
      valid_from: deal.dates.valid_from ?? null,
      valid_until: deal.dates.valid_until ?? null,
      raw_text: deal.dates.raw_text ?? null,
    };
  }
  
  // Fallback to flat format
  return {
    valid_from: deal.valid_from ?? null,
    valid_until: deal.valid_until ?? null,
    raw_text: null,
  };
}

/**
 * Get value object with proper unwrapping
 */
export function getValue(payload: unknown): { 
  bonus_value_cad?: number | null; 
  qualifying_spend_cad?: number | null;
  percent_off?: number | null;
  points_amount?: number | null;
} {
  const deal = getValidatedDeal(payload);
  
  // Prefer nested value object
  if (deal.value) {
    return {
      bonus_value_cad: deal.value.bonus_value_cad ?? null,
      qualifying_spend_cad: deal.value.qualifying_spend_cad ?? null,
      percent_off: deal.value.percent_off ?? null,
      points_amount: deal.value.points_amount ?? null,
    };
  }
  
  // Fallback to flat format
  return {
    bonus_value_cad: deal.bonus_value_cad ?? deal.reward_amount ?? null,
    qualifying_spend_cad: deal.qualifying_spend_cad ?? deal.purchase_amount ?? null,
    percent_off: deal.percent_off ?? deal.discount_percent ?? null,
    points_amount: deal.points_amount ?? null,
  };
}

/**
 * Get confidence with proper unwrapping
 */
export function getConfidence(payload: unknown): number | undefined {
  const deal = getValidatedDeal(payload);
  return deal.confidence;
}

/**
 * CR226 Scope C: Extract deal data from evidence_json.raw_deal as final fallback
 * This is used when scalar columns and validated_payload_json both lack data.
 * 
 * The evidence_json typically contains:
 * - raw_deal: The original extracted deal object from AI scan
 * - raw_ai_response_json: The raw AI API response
 * 
 * @param evidenceJson The evidence_json value from cr075_staging_deal_items
 * @returns An ExtractedDeal object from raw_deal, or empty object if not present
 */
export function getEvidenceRawDeal(evidenceJson: unknown): ExtractedDeal {
  if (!evidenceJson || typeof evidenceJson !== 'object') {
    return {};
  }
  
  const evidence = evidenceJson as Record<string, unknown>;
  
  // Check for raw_deal at top level
  if (evidence.raw_deal && typeof evidence.raw_deal === 'object') {
    return evidence.raw_deal as ExtractedDeal;
  }
  
  return {};
}

/**
 * CR226 Scope C: Get headline with evidence_json fallback
 */
export function getHeadlineWithFallback(
  scalarValue: string | null | undefined,
  payload: unknown,
  evidenceJson: unknown
): string | undefined {
  // Tier 1: Scalar column (unless it's a generic placeholder)
  if (scalarValue && !scalarValue.toLowerCase().includes('deal on gift card')) {
    return scalarValue;
  }
  
  // Tier 2: validated_payload_json
  const fromPayload = getHeadlineMechanic(payload);
  if (fromPayload) {
    return fromPayload;
  }
  
  // Tier 3: evidence_json.raw_deal
  const rawDeal = getEvidenceRawDeal(evidenceJson);
  return rawDeal.headline_mechanic || rawDeal.raw_offer_text;
}

/**
 * CR226 Scope C: Get dates with evidence_json fallback
 */
export function getDatesWithFallback(
  scalarStart: string | null | undefined,
  scalarEnd: string | null | undefined,
  payload: unknown,
  evidenceJson: unknown
): { valid_from?: string | null; valid_until?: string | null } {
  // Tier 1: Scalar columns
  if (scalarStart || scalarEnd) {
    return {
      valid_from: scalarStart ?? null,
      valid_until: scalarEnd ?? null,
    };
  }
  
  // Tier 2: validated_payload_json
  const fromPayload = getDates(payload);
  if (fromPayload.valid_from || fromPayload.valid_until) {
    return fromPayload;
  }
  
  // Tier 3: evidence_json.raw_deal
  const rawDeal = getEvidenceRawDeal(evidenceJson);
  if (rawDeal.dates) {
    return {
      valid_from: rawDeal.dates.valid_from ?? null,
      valid_until: rawDeal.dates.valid_until ?? null,
    };
  }
  
  return {
    valid_from: rawDeal.valid_from ?? null,
    valid_until: rawDeal.valid_until ?? null,
  };
}

/**
 * CR226 Scope C: Get value with evidence_json fallback
 */
export function getValueWithFallback(
  scalarPurchaseAmount: number | null | undefined,
  scalarRewardAmount: number | null | undefined,
  payload: unknown,
  evidenceJson: unknown
): { 
  bonus_value_cad?: number | null; 
  qualifying_spend_cad?: number | null;
  percent_off?: number | null;
  points_amount?: number | null;
} {
  // Tier 1: Scalar columns
  if (scalarPurchaseAmount !== null || scalarRewardAmount !== null) {
    return {
      bonus_value_cad: scalarRewardAmount ?? null,
      qualifying_spend_cad: scalarPurchaseAmount ?? null,
      percent_off: null,
      points_amount: null,
    };
  }
  
  // Tier 2: validated_payload_json
  const fromPayload = getValue(payload);
  if (fromPayload.bonus_value_cad || fromPayload.qualifying_spend_cad || fromPayload.percent_off) {
    return fromPayload;
  }
  
  // Tier 3: evidence_json.raw_deal
  const rawDeal = getEvidenceRawDeal(evidenceJson);
  if (rawDeal.value) {
    return {
      bonus_value_cad: rawDeal.value.bonus_value_cad ?? null,
      qualifying_spend_cad: rawDeal.value.qualifying_spend_cad ?? null,
      percent_off: rawDeal.value.percent_off ?? null,
      points_amount: rawDeal.value.points_amount ?? null,
    };
  }
  
  return {
    bonus_value_cad: rawDeal.bonus_value_cad ?? rawDeal.reward_amount ?? null,
    qualifying_spend_cad: rawDeal.qualifying_spend_cad ?? rawDeal.purchase_amount ?? null,
    percent_off: rawDeal.percent_off ?? rawDeal.discount_percent ?? null,
    points_amount: rawDeal.points_amount ?? null,
  };
}

/**
 * CR226 Scope C: Get purchase brand with evidence_json fallback
 */
export function getPurchaseBrandWithFallback(
  scalarValue: string | null | undefined,
  payload: unknown,
  evidenceJson: unknown
): string | undefined {
  // Tier 1: Scalar column
  if (scalarValue) {
    return scalarValue;
  }
  
  // Tier 2: validated_payload_json
  const fromPayload = getPurchaseBrand(payload);
  if (fromPayload) {
    return fromPayload;
  }
  
  // Tier 3: evidence_json.raw_deal
  const rawDeal = getEvidenceRawDeal(evidenceJson);
  return rawDeal.purchase_brand || 
         rawDeal.purchase_gift_card_brand || 
         rawDeal.brands?.purchase_brand || 
         undefined;
}

/**
 * CR226 Scope C: Get deal type with evidence_json fallback
 */
export function getDealTypeWithFallback(
  scalarValue: string | null | undefined,
  payload: unknown,
  evidenceJson: unknown
): string | undefined {
  // Tier 1: Scalar column
  if (scalarValue) {
    return scalarValue;
  }
  
  // Tier 2: validated_payload_json
  const fromPayload = getDealType(payload);
  if (fromPayload) {
    return fromPayload;
  }
  
  // Tier 3: evidence_json.raw_deal
  const rawDeal = getEvidenceRawDeal(evidenceJson);
  return rawDeal.deal_type;
}

/**
 * CR226 Scope C: Get variants with evidence_json fallback
 */
export function getVariantsWithFallback(
  payload: unknown,
  evidenceJson: unknown
): ExtractedDeal['variants'] {
  // Tier 1: validated_payload_json
  const fromPayload = getVariants(payload);
  if (fromPayload && fromPayload.length > 0) {
    return fromPayload;
  }
  
  // Tier 2: evidence_json.raw_deal
  const rawDeal = getEvidenceRawDeal(evidenceJson);
  return rawDeal.variants || [];
}
